import { createBrowserRouter, Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import routes from "./routes";
import ConditionalRoute from "@/components/ConditionalRoute/ConditionalRoute";
import PreLogin from "@/layouts/PreLogin/PreLogin";
import PostLogin from "@/layouts/PostLogin/PostLogin";
import PageNotFound from "@/pages/PageNotFound/PageNotFound";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";

export interface RouteConfig {
  path: string;
  element?: React.ReactNode; // make optional
  children?: Record<string, RouteConfig> | RouteConfig[];
  pageName?: string;
  sidebar?: { show: boolean };
  permissionName?: string;
  requiredModule?: string;
  requiredRole?: string;
}

const buildRoutes = (
  routesInput: Record<string, RouteConfig> | RouteConfig[]
): RouteObject[] => {
  const routesArray: RouteConfig[] = Array.isArray(routesInput)
    ? routesInput
    : Object.values(routesInput);

  return routesArray.map(({ path, element, children }) => ({
    path,
    ...(element ? { element } : {}), // only include element if it exists
    ...(children ? { children: buildRoutes(children) } : {}),
  }));
};

/**
 * Build private routes with PermissionGuard wrapping.
 * Each route's element is wrapped so permission checks happen at render time.
 */
const buildPrivateRoutes = (
  routesInput: Record<string, RouteConfig> | RouteConfig[]
): RouteObject[] => {
  const routesArray: RouteConfig[] = Array.isArray(routesInput)
    ? routesInput
    : Object.values(routesInput);

  return routesArray.map(({ path, element, children, requiredModule, permissionName, requiredRole }) => ({
    path,
    ...(element
      ? {
          element: (
            <PermissionGuard
              requiredModule={requiredModule}
              permissionName={permissionName}
              requiredRole={requiredRole}
            >
              {element}
            </PermissionGuard>
          ),
        }
      : {}),
    ...(children ? { children: buildPrivateRoutes(children) } : {}),
  }));
};


const router = createBrowserRouter([
  // Redirect root to login
  {
    path: "/",
    element: (
      <Navigate
        to={routes?.publicRoutes ? routes.publicRoutes.LOGIN.path : "/"}
        replace
      />
    ),
  },

  // Open routes
  ...buildRoutes(routes.routes),

  // Public routes
  {
    path: "/",
    element: <ConditionalRoute component={PreLogin} />,
    children: [
      {
        index: true,
        element: (
          <Navigate
            to={routes?.publicRoutes ? routes.publicRoutes.LOGIN.path : "/"}
          />
        ),
      },
      ...buildRoutes(routes.publicRoutes),
    ],
  },

  // Private routes — wrapped with PermissionGuard
  {
    path: "/",
    element: <ConditionalRoute isPrivate component={PostLogin} />,
    children: [
      ...buildPrivateRoutes(routes.privateRoutes),
      {
        index: true,
        element: <Navigate to={routes.privateRoutes.DASHBOARD.path} />,
      },
    ],
  },

  // Catch-all
  {
    path: "*",
    element: <PageNotFound />,
  },
]);

export default router;
